/**
 * Mirror every dirty write into the native outbox (DATA_MODEL §2.3).
 *
 * A Dexie DBCore middleware, registered in `db.ts` only where the bridge has an
 * outbox (the phone). It watches the synced tables and records, per IndexedDB
 * transaction:
 *
 * - a `put`/`add` of a row with `_dirty === 1` — an outbox entry, in exactly
 *   the bytes `sync.ts` will push (`toWirePayload`);
 * - a `delete` — the entry's removal. This is what makes Undo safe: undoing an
 *   expense that never left the device hard-deletes it (`repo.ts`, the INV-5
 *   exemption), and without this the next launch's refill would bring it back.
 *
 * A `put` with `_dirty === 0` is sync marking a row clean, and the ack in
 * `sync.ts` owns that. `deleteRange` (a table being cleared) is ignored: the
 * clearing paths in `session.ts` clear the outbox themselves.
 *
 * **It flushes on the transaction's `complete`, never inside it.** Awaiting a
 * promise that is not IndexedDB's within an IndexedDB transaction lets the
 * transaction auto-commit underneath the code still using it — and the native
 * call is exactly such a promise. Flushing after commit also means a rolled-back
 * write is never mirrored. And it never delays the write itself: capture
 * returns when IndexedDB has the row (INV-4), and the mirror follows.
 */

import type { DBCore, DBCoreMutateRequest, DBCoreMutateResponse, DBCoreTransaction, Middleware } from "dexie";

import type { OutboxEntry, OutboxKey } from "@/native/bridge";

import type { OutboxStore } from "./outbox";
import { toWirePayload } from "./synced_tables";

interface SyncedRow {
  id: string;
  updatedAt: string;
  deletedAt?: string | null;
  _dirty?: number;
}

function isSyncedRow(value: unknown): value is SyncedRow {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Record<string, unknown>;
  return typeof row.id === "string" && typeof row.updatedAt === "string";
}

/** The middleware's name in Dexie's stack — registering again replaces it. */
export const OUTBOX_MIDDLEWARE_NAME = "native-outbox";

/** The final operation per row in one transaction: a later write replaces an earlier one. */
type Pending = Map<string, { kind: "put"; entry: OutboxEntry } | { kind: "remove"; key: OutboxKey }>;

export function outboxMiddleware(
  store: OutboxStore,
  tables: Readonly<Record<string, string>>,
  ledger?: "family",
): Middleware<DBCore> {
  let flushQueue = Promise.resolve();
  const pendingByTransaction = new WeakMap<DBCoreTransaction, Pending>();

  async function flush(pending: Pending): Promise<void> {
    const puts: OutboxEntry[] = [];
    const removes: OutboxKey[] = [];
    for (const op of pending.values()) {
      if (op.kind === "put") puts.push(op.entry);
      else removes.push(op.key);
    }
    try {
      if (removes.length > 0) await store.remove(removes);
      if (puts.length > 0) await store.put(puts);
    } catch (cause) {
      // Loud, not fatal (INV-7): the row is safe in IndexedDB and will upload
      // from there; what is lost is only the eviction safety net for it.
      console.warn(
        "Could not mirror a write into the native outbox:",
        cause instanceof Error ? cause.message : cause,
      );
    }
  }

  function pendingFor(trans: DBCoreTransaction): Pending {
    const existing = pendingByTransaction.get(trans);
    if (existing !== undefined) return existing;
    const pending: Pending = new Map();
    pendingByTransaction.set(trans, pending);
    const target = trans as unknown as Partial<EventTarget>;
    if (typeof target.addEventListener === "function") {
      target.addEventListener("complete", () => {
        flushQueue = flushQueue.then(() => flush(pending));
      }, { once: true });
    } else {
      // Never guess a commit: an unknown transaction cannot be mirrored safely.
      console.warn("Native outbox cannot observe this transaction's completion.");
    }
    return pending;
  }

  function record(remote: string, req: DBCoreMutateRequest, res: DBCoreMutateResponse): void {
    if (req.type === "deleteRange") return;
    const failed = (index: number): boolean => index in res.failures;

    if (req.type === "delete") {
      const pending = pendingFor(req.trans);
      req.keys.forEach((key: unknown, index) => {
        if (failed(index) || typeof key !== "string") return;
        pending.set(`${remote}\u0000${key}`, { kind: "remove", key: { table: remote, rowId: key, ...(ledger ? { ledger } : {}) } });
      });
      return;
    }

    req.values.forEach((value: unknown, index) => {
      if (failed(index) || !isSyncedRow(value) || value._dirty !== 1) return;
      pendingFor(req.trans).set(`${remote}\u0000${value.id}`, {
        kind: "put",
        entry: {
          ...(ledger ? { ledger } : {}),
          table: remote,
          rowId: value.id,
          updatedAt: value.updatedAt,
          deleted: value.deletedAt != null,
          payload: toWirePayload(value),
        },
      });
    });
  }

  return {
    stack: "dbcore",
    name: OUTBOX_MIDDLEWARE_NAME,
    create: (down) => ({
      ...down,
      table(name) {
        const table = down.table(name);
        const remote = tables[name];
        if (remote === undefined) return table;
        return {
          ...table,
          async mutate(req) {
            const res = await table.mutate(req);
            record(remote, req, res);
            return res;
          },
        };
      },
    }),
  };
}
